import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Container,
  Chip,
  Tooltip,
  Skeleton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CommentIcon from "@mui/icons-material/Comment";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { alpha, darken } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { fetchComments, deleteComment } from "../redux/commentSlice";
import axios from "axios";

const SKELETON_ROWS = 6;

const AdminCommentsPage = () => {
  const dispatch = useDispatch();
  const { items: comments = [], loading } = useSelector((s) => s.comments);

  const [postTitles, setPostTitles] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    dispatch(fetchComments());
  }, [dispatch]);

  useEffect(() => {
    if (comments.length > 0) {
      const uniquePostIds = [
        ...new Set(comments.map((c) => c.postId?.toString?.())),
      ];
      const titleMap = {};

      Promise.all(
        uniquePostIds.map(async (postId) => {
          try {
            const res = await axios.get(
              `https://api.dilanaslan.com/api/posts/${postId}`
            );
            titleMap[postId] = res.data.title;
          } catch {
            titleMap[postId] = "Bilinmeyen Yazı";
          }
        })
      ).then(() => setPostTitles(titleMap));
    }
  }, [comments]);

  const handleDelete = async () => {
    await dispatch(deleteComment(deleteId));
    setConfirmDelete(false);
    setDeleteId(null);
  };

  return (
    <Container
      maxWidth="lg"
      sx={{ py: { xs: 4, md: 5 }, position: "relative" }}
    >
      {/* HEADER */}
      <Paper
        elevation={0}
        sx={(t) => ({
          mb: 4,
          p: 3,
          borderRadius: 4,
          backdropFilter: "blur(18px)",
          background: alpha(t.palette.background.paper, 0.85),
          border: `1px solid ${alpha(t.palette.divider, 0.3)}`,
          position: "relative",
          overflow: "hidden",
          "&:before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: `linear-gradient(120deg, ${alpha(
              t.palette.primary.main,
              0.09
            )}, transparent 60%)`,
            pointerEvents: "none",
          },
        })}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={(t) => ({
              width: 52,
              height: 52,
              borderRadius: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: alpha(t.palette.primary.main, 0.15),
              border: `1px solid ${alpha(t.palette.primary.main, 0.25)}`,
              flexShrink: 0,
            })}
          >
            <CommentIcon color="primary" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                letterSpacing: "-0.5px",
                lineHeight: 1.08,
              }}
            >
              Yorumlar
            </Typography>
            <Box sx={{ mt: 0.6, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                size="small"
                icon={<CommentIcon sx={{ fontSize: 18 }} />}
                label={`${comments.length} Yorum`}
                sx={{
                  height: 30,
                  fontWeight: 600,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                  color: "primary.main",
                  ".MuiChip-label": { px: 1.2 },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* TABLE */}
      <Paper
        elevation={0}
        sx={(t) => ({
          borderRadius: 4,
          backdropFilter: "blur(14px)",
          background: alpha(t.palette.background.paper, 0.85),
          border: `1px solid ${alpha(t.palette.divider, 0.3)}`,
          overflow: "hidden",
        })}
      >
        <TableContainer sx={{ maxHeight: 620 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Kullanıcı</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Yorum</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Yazı Başlığı</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tarih</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 100 }}>
                  Beğeni
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, width: 120 }}>
                  İşlemler
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading &&
                Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell>
                      <Skeleton width="40%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton width="80%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton width="50%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={80} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={30} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton width={60} />
                    </TableCell>
                  </TableRow>
                ))}

              {!loading && comments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 8 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        opacity: 0.75,
                      }}
                    >
                      <CommentIcon color="disabled" sx={{ fontSize: 50 }} />
                      <Typography variant="body2" color="text.secondary">
                        Hiç yorum bulunamadı.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                comments.map((comment) => (
                  <TableRow
                    key={comment._id}
                    hover
                    sx={{
                      transition: ".25s",
                      "&:hover": {
                        backgroundColor: (t) =>
                          alpha(t.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <TableCell>{comment.user?.username || "Anonim"}</TableCell>
                    <TableCell>{comment.text}</TableCell>
                    <TableCell>
                      {postTitles[comment.postId?.toString?.()] ||
                        "Yükleniyor..."}
                    </TableCell>
                    <TableCell>
                      {new Date(comment.date).toLocaleDateString("tr-TR")}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <FavoriteIcon fontSize="small" color="error" />
                        <Typography variant="body2">
                          {comment.likes?.length || 0}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Sil" arrow>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDeleteId(comment._id);
                            setConfirmDelete(true);
                          }}
                          sx={(t) => ({
                            width: 36,
                            height: 36,
                            borderRadius: 3,
                            color: "error.main",
                            bgcolor: alpha(t.palette.error.main, 0.12),
                            "&:hover": {
                              bgcolor: alpha(t.palette.error.main, 0.2),
                            },
                          })}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* DELETE CONFIRM DIALOG */}
      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            width: "100%",
            maxWidth: 400,
            p: 0,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Yorumu Sil
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Bu işlem geri alınamaz
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography fontSize={13}>
            Seçili yorumu kalıcı olarak silmek istiyor musunuz?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setConfirmDelete(false)}
            sx={{ textTransform: "none", borderRadius: 3 }}
          >
            Vazgeç
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            sx={(t) => ({
              textTransform: "none",
              borderRadius: 3,
              fontWeight: 600,
              background: `linear-gradient(90deg, ${
                t.palette.error.main
              }, ${darken(t.palette.error.main, 0.18)})`,
            })}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminCommentsPage;
